import { ChevronLeft, ChevronRight } from 'lucide-react';
import { memo } from 'react';
import {
  LoadingFooter,
  SlowLoadNotice,
  TableSkeletonBody,
  TopProgressBar,
  useTableLoading,
} from '../UI/loading';

const NAV_BTN_CLASS =
  '!p-1.5 rounded-md !border border-gray-300 border-solid bg-white hover:!bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors';

// 0-based page indexes to render, with 'ellipsis' gaps: 1 … 4 5 6 … 20
const getPageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set([0, total - 1, current - 1, current, current + 1]);
  if (current <= 2) [1, 2, 3].forEach((p) => pages.add(p));
  if (current >= total - 3) [total - 4, total - 3, total - 2].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) items.push(`ellipsis-${p}`);
    items.push(p);
  });
  return items;
};

export const PaginationMainTable = memo(function PaginationMainTable({
  children,
  pageSize,
  setPageSize,
  pageSizeOptions = [25, 50, 100],
  hasNext,
  hasPrev,
  onNextPage,
  onPreviousPage,
  isLoadingPage,
  loading = isLoadingPage,
  loadingLabel = 'Loading',
  onCancelLoading,
  cancelled = false,
  cancelledMessage = 'Search cancelled. Adjust filters and search again.',
  pageNo,
  totalCount,
  wrapperClassName,
  showCount = true,
  // Numbered mode (opt-in): pass onPageChange + totalPages to render
  // "Showing x–y of N <countNoun>" on the left and page-number buttons.
  onPageChange,
  totalPages = 0,
  countNoun = '',
}) {
  const numbered = typeof onPageChange === 'function';
  const { isLoading, showLoading, tableProps } = useTableLoading(loading, cancelled);
  const skeletonRows = pageSize > 0 && pageSize < 10 ? pageSize : 10;

  const pageSizeSelect = (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-600">Rows per page:</span>
      <select
        value={pageSize}
        onChange={(e) => setPageSize(Number(e.target.value))}
        className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
        disabled={isLoading}
      >
        {pageSizeOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  // The top bar is positioned against the wrapper, so it is always `relative`.
  return (
    <div className={`relative ${wrapperClassName ?? 'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'}`}>
      <TopProgressBar active={showLoading} />
      <div role="status" aria-live="polite" className="sr-only">
        {showLoading ? loadingLabel : ''}
      </div>
      <div className="w-full overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="aone-table min-w-full w-full text-sm" {...tableProps}>
          {children}
          {showLoading && <TableSkeletonBody rows={skeletonRows} />}
          {cancelled && !isLoading && (
            <tbody className="aone-skel-body">
              <tr>
                <td colSpan={100} className="px-6 py-12 text-center text-gray-500">
                  {cancelledMessage}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <SlowLoadNotice active={isLoading} onCancel={onCancelLoading} className="m-3" />

      {/* Pagination Controls Footer */}
      {showLoading ? (
        <LoadingFooter label={loadingLabel} />
      ) : numbered ? (
        <div className="border-t border-gray-200 border-solid border-b-0 border-x-0 bg-gray-50 px-6 !py-3 flex flex-wrap items-center justify-between gap-4">
          <div className="text-xs text-gray-600">
            {totalCount > 0
              ? `Showing ${(pageNo * pageSize + 1).toLocaleString()}–${Math.min(pageNo * pageSize + pageSize, totalCount).toLocaleString()} of ${totalCount.toLocaleString()}${countNoun ? ` ${countNoun}` : ''}`
              : null}
          </div>
          <div className="flex items-center gap-6">
            {pageSizeSelect}
            <nav className="flex items-center gap-1" aria-label="Pagination">
              <button
                onClick={() => onPageChange(pageNo - 1)}
                disabled={pageNo <= 0 || isLoading}
                className={NAV_BTN_CLASS}
                title="Go to previous page"
                aria-label="Go to previous page"
              >
                <ChevronLeft className="size-4 text-gray-600" />
              </button>
              {getPageItems(pageNo, totalPages).map((item) =>
                typeof item === 'string' ? (
                  <span key={item} className="px-1 text-xs text-gray-500" aria-hidden="true">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => onPageChange(item)}
                    disabled={isLoading}
                    aria-current={item === pageNo ? 'page' : undefined}
                    aria-label={`Page ${item + 1}`}
                    className={`min-w-[28px] h-7 px-2 rounded-md text-xs transition-colors ${
                      item === pageNo
                        ? 'bg-[#EC171C] text-white font-medium'
                        : 'text-gray-700 bg-white !border border-gray-300 border-solid hover:!bg-gray-100'
                    }`}
                  >
                    {item + 1}
                  </button>
                )
              )}
              <button
                onClick={() => onPageChange(pageNo + 1)}
                disabled={pageNo >= totalPages - 1 || isLoading}
                className={NAV_BTN_CLASS}
                title="Go to next page"
                aria-label="Go to next page"
              >
                <ChevronRight className="size-4 text-gray-600" />
              </button>
            </nav>
          </div>
        </div>
      ) : (
        <div className="h-[52px] border-t border-gray-200 border-solid border-b-0 border-x-0 bg-gray-50 px-6 flex items-center justify-end gap-6">
          {pageSizeSelect}

          {showCount && (
            <div className="text-xs text-gray-600">
              {totalCount > 0 ?
                `${pageNo * pageSize + 1} - ${Math.min(pageNo * pageSize + pageSize, totalCount)} of ${totalCount}` :
                null}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onPreviousPage}
              disabled={!hasPrev || isLoading}
              className={NAV_BTN_CLASS}
              title="Go to previous page"
              aria-label="Go to previous page"
            >
              <ChevronLeft className="size-4 text-gray-600" />
            </button>

            <button
              onClick={onNextPage}
              disabled={!hasNext || isLoading}
              className={NAV_BTN_CLASS}
              title="Go to next page"
              aria-label="Go to next page"
            >
              <ChevronRight className="size-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
